import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { dictionary, localizePath } from "@/i18n/translations";
import { api, buildAuthHeaders } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

export default function DashboardLeadsPage({ lang }) {
  const copy = dictionary[lang];
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [updatingLeadId, setUpdatingLeadId] = useState("");

  const statusFilter = searchParams.get("status") || "all";
  const page = Math.max(1, Number(searchParams.get("page") || "1"));

  const statusOptions = useMemo(
    () => [
      { key: "new", label: copy.dashboardLeads.statusNew },
      { key: "in_progress", label: copy.dashboardLeads.statusInProgress },
      { key: "closed", label: copy.dashboardLeads.statusClosed },
    ],
    [copy.dashboardLeads.statusClosed, copy.dashboardLeads.statusInProgress, copy.dashboardLeads.statusNew],
  );

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.dashboardNav.leads} | ${copy.nav.brand}`,
      description: copy.dashboardLeads.subtitle,
    });
  }, [copy.dashboardLeads.subtitle, copy.dashboardNav.leads, copy.nav.brand, lang]);

  const fetchLeads = async () => {
    if (user?.role !== "admin") {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("page_size", "12");
      if (statusFilter !== "all") {
        query.set("status_filter", statusFilter);
      }

      const response = await api.get(`/admin/leads?${query.toString()}`, {
        headers: buildAuthHeaders(session),
      });

      setItems(response.data?.items || []);
      setPagination({
        page: response.data?.page || 1,
        total_pages: response.data?.total_pages || 1,
        total: response.data?.total || 0,
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [page, statusFilter, user?.role]);

  const updateQueryParams = (next) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(next).forEach(([key, value]) => {
      const text = String(value ?? "").trim();
      if (!text || text === "all" || text === "1") {
        params.delete(key);
      } else {
        params.set(key, text);
      }
    });
    setSearchParams(params);
  };

  const updateStatus = async (leadId, status) => {
    setUpdatingLeadId(leadId);
    try {
      await api.patch(
        `/admin/leads/${leadId}/status`,
        { status },
        { headers: buildAuthHeaders(session) },
      );
      toast.success(copy.dashboardLeads.statusUpdated);
      await fetchLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    } finally {
      setUpdatingLeadId("");
    }
  };

  const openLeadDetails = (leadId) => {
    const back = encodeURIComponent(searchParams.toString());
    navigate(`${localizePath(lang, `/dashboard/leads/${leadId}`)}?back=${back}`);
  };

  if (user?.role !== "admin") {
    return (
      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-leads-forbidden-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-leads-forbidden-title">{copy.dashboardNav.leads}</CardTitle>
        </CardHeader>
        <CardContent data-testid="dashboard-leads-forbidden-message">{copy.dashboardLeads.adminOnly}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-leads-page">
      <section className="space-y-2" data-testid="dashboard-leads-header">
        <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="dashboard-leads-title">
          {copy.dashboardNav.leads}
        </h1>
        <p className="text-sm text-stone-600" data-testid="dashboard-leads-subtitle">
          {copy.dashboardLeads.subtitle}
        </p>
      </section>

      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-leads-toolbar-card">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="dashboard-leads-toolbar-content">
          <p className="text-sm text-stone-600" data-testid="dashboard-leads-total-count">
            {pagination.total}
          </p>

          <Select value={statusFilter} onValueChange={(value) => updateQueryParams({ status: value, page: "1" })}>
            <SelectTrigger className="w-[220px]" data-testid="dashboard-leads-status-filter-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="dashboard-leads-status-filter-all">{copy.dashboardLeads.allStatuses}</SelectItem>
              {statusOptions.map((option) => (
                <SelectItem value={option.key} key={option.key} data-testid={`dashboard-leads-status-filter-${option.key}`}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <section className="grid gap-4" data-testid="dashboard-leads-list">
        {loading && <p data-testid="dashboard-leads-loading">{copy.common.loading}</p>}

        {!loading && items.length === 0 && (
          <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-leads-empty-card">
            <CardHeader>
              <CardTitle data-testid="dashboard-leads-empty-title">{copy.dashboardLeads.emptyTitle}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="dashboard-leads-empty-content">
              <p className="text-sm text-stone-600" data-testid="dashboard-leads-empty-description">
                {copy.dashboardLeads.emptyDescription}
              </p>
              <Link to={localizePath(lang, "/contact")} data-testid="dashboard-leads-go-contact-link">
                <Button data-testid="dashboard-leads-go-contact-button">{copy.footer.quickLinks.contact}</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {!loading &&
          items.map((lead) => (
            <Card key={lead.id} className="border-gold-500/20 bg-white/95" data-testid={`dashboard-leads-card-${lead.id}`}>
              <CardContent className="space-y-4 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3" data-testid={`dashboard-leads-card-header-${lead.id}`}>
                  <div>
                    <p className="text-lg font-semibold text-stone-900" data-testid={`dashboard-leads-card-name-${lead.id}`}>{lead.full_name}</p>
                    <p className="text-xs text-stone-600" data-testid={`dashboard-leads-card-email-${lead.id}`}>{lead.email}</p>
                    <p className="text-xs text-stone-600" data-testid={`dashboard-leads-card-phone-${lead.id}`}>{lead.phone}</p>
                  </div>

                  <Badge variant="outline" data-testid={`dashboard-leads-card-source-${lead.id}`}>
                    {copy.dashboardLeads.sourceContactPage}
                  </Badge>
                </div>

                <p className="line-clamp-2 text-sm text-stone-700" data-testid={`dashboard-leads-card-message-${lead.id}`}>
                  {lead.message}
                </p>

                <div className="flex flex-wrap items-center justify-between gap-2" data-testid={`dashboard-leads-card-actions-${lead.id}`}>
                  <div className="flex items-center gap-2">
                    <Select value={lead.status} onValueChange={(value) => updateStatus(lead.id, value)} disabled={updatingLeadId === lead.id}>
                      <SelectTrigger className="w-[180px]" data-testid={`dashboard-leads-card-status-trigger-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => (
                          <SelectItem value={option.key} key={option.key} data-testid={`dashboard-leads-card-status-${lead.id}-${option.key}`}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-stone-500" data-testid={`dashboard-leads-card-date-${lead.id}`}>{lead.created_at?.split("T")?.[0]}</span>
                  </div>

                  <Button variant="outline" onClick={() => openLeadDetails(lead.id)} data-testid={`dashboard-leads-card-view-${lead.id}`}>
                    {copy.dashboardLeads.viewDetails}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
      </section>

      <div className="flex items-center justify-between rounded-2xl border border-gold-500/20 bg-white/80 px-4 py-3" data-testid="dashboard-leads-pagination-bar">
        <Button variant="outline" disabled={pagination.page <= 1} onClick={() => updateQueryParams({ page: String(pagination.page - 1) })} data-testid="dashboard-leads-pagination-prev-button">
          {copy.dashboardProperties.previous}
        </Button>
        <span className="text-sm text-stone-700" data-testid="dashboard-leads-pagination-info">
          {pagination.page} / {pagination.total_pages}
        </span>
        <Button variant="outline" disabled={pagination.page >= pagination.total_pages} onClick={() => updateQueryParams({ page: String(pagination.page + 1) })} data-testid="dashboard-leads-pagination-next-button">
          {copy.dashboardProperties.next}
        </Button>
      </div>
    </div>
  );
}
